/** @jsx jsx */
import { Button, Grid, Hr, Modal, Spacer, TextGroupMedium } from "@cd2/components"
import { P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import { format } from "date-fns"
import React from "react"
import { TextWithNewLines } from "../Components/TextWithNewLines"
import { IRecord } from "../types/schema"
import { useConfirmation } from "./useConfirmation"
import { titleize } from "@cd2/tools"
import { createPortal } from "react-dom"

const baseAttrs = [
  "message",
  "display_as_alert",
  "imported_from_sage",
  "created_at",
  "updated_at",
] as const
const assocAttrs = [{ name: "created_by", attributes: ["name"] }]
export const memoDisplayAttributes = [...baseAttrs, ...assocAttrs] as any

export type IMemoForDisplay = IRecord<"customer_memos", (typeof baseAttrs)[number]> & {
  created_by: IRecord<"skeem_users", "name">
}
type ICustomer = IRecord<"customers", "name"> & {
  customer_memos: IMemoForDisplay[]
}

export function MemoCard({ memo }: { memo: IMemoForDisplay }) {
  return (
    <div
      key={memo.id}
      css={css`
        border-radius: 8px;
        border: 1px solid #e5e5e5;
        padding: 16px;
      `}
    >
      <Grid gap={8} columns={["max-content", "auto"]}>
        <P blockSpacing={0} weight={600}>
          {memo.created_by?.name ?? "Created by the system"}
        </P>
        <P blockSpacing={0}>
          {memo.created_at ? format(new Date(memo.created_at), "dd/MM/yyyy HH:mm") : ""}
        </P>
      </Grid>
      {memo.imported_from_sage && <P size="small">This memo was imported from sage.</P>}
      <Spacer size={8} />
      <TextWithNewLines text={memo.message} />
    </div>
  )
}

export function useCustomerMemo() {
  const skeem = window.skeem
  const portalNode = document.getElementById("app-scene-portal-ref")!
  const [customerMemoNode, alertCustomerMemo] = useConfirmation<
    ICustomer & { customer_memos: IMemoForDisplay[]; display_type?: "memos" | "alerts" },
    void
  >(({ onResolve, data }) => {
    const displayName = data.display_type ? titleize(data.display_type) : "Memos"
    return createPortal(
      <Modal
        isOpen={true}
        onRequestClose={() => onResolve()}
        css={css`
          max-width: calc(100% - 16px);
          overflow: initial;
          text-align: left;
        `}
        render={() => (
          <React.Fragment>
            <TextGroupMedium
              heading={`${displayName} for ${data.name}`}
              body={`Manage ${displayName.toLowerCase()} on the customer edit page.`}
              theme={{ heading: { size: "small" } }}
            />
            <Spacer size={8} />
            <Hr />
            <Spacer size={8} />
            <Grid
              gap={8}
              css={css`
                max-height: 500px;
                overflow: auto;
                padding-right: 4px;
              `}
            >
              {data.customer_memos.map(memo => (
                <MemoCard key={memo.id} memo={memo} />
              ))}
            </Grid>
            <Spacer size={8} />
            <Hr />
            <Spacer size={8} />
            <div>
              <Button onClick={() => onResolve()} buttonColor="white">
                Okay
              </Button>
            </div>
          </React.Fragment>
        )}
      />,
      portalNode,
      "useCustomerMemo",
    )
  })

  async function handleLoadMemos(
    customerId?: string,
    options?: { noModal?: boolean; allMemos?: boolean; onlyOfType?: "memos" | "alerts" },
  ) {
    if (!customerId) {
      return null
    }
    const customer = await skeem.fetch2<ICustomer>({
      first: {
        name: "customers",
        attributes: [
          "name",
          {
            name: "customer_memos",
            attributes: memoDisplayAttributes,
            sort: { by: "created_at", direction: "desc" },
            filter: options?.onlyOfType
              ? { matches: { display_as_alert: options.onlyOfType === "alerts" } }
              : undefined,
          },
        ],
        filter: { matches: { id: customerId } },
      },
    })

    if (!options?.onlyOfType) {
      customer.customer_memos = customer.customer_memos.filter(
        m => options?.allMemos || m.display_as_alert,
      )
    }

    if (customer.customer_memos.length > 0 && !options?.noModal) {
      await alertCustomerMemo({ ...customer, display_type: options?.onlyOfType })
    }
    return customer
  }

  return { customerMemoNode, alertCustomerMemos: handleLoadMemos }
}
